import { useState, useCallback, useRef, useEffect } from 'react';
import { makeApiCall } from '../utils/api';
import { API_PATHS } from '../config/api-endpoints';

interface UseFeedbackGenerationProps {
    jobId: string;
    onSuccess?: (templateData: any) => void;
    onError?: (error: string) => void;
}

interface TemplateStatus {
    templateId: string;
    status: 'GENERATING' | 'VALIDATED' | 'FAILED' | 'GENERATION_FAILED' | 'SUBMITTED' | 'PENDING';
    templateUri?: string;
    templateS3Uri?: string;
    error?: string;
    errorMessage?: string;  // Detailed error message from backend
    mermaidDiagram?: string;
    aiSummary?: string;
}

export const useFeedbackGeneration = ({ 
    jobId, 
    onSuccess, 
    onError 
}: UseFeedbackGenerationProps) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'polling' | 'success' | 'failed'>('idle');
    const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [generationProgress, setGenerationProgress] = useState(0);
    
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number | null>(null);

    // Calculate word count
    const handleFeedbackChange = useCallback((text: string) => {
        setFeedbackText(text);
        // Count words (split by whitespace and filter empty strings)
        const words = text.trim().split(/\s+/).filter(word => word.length > 0);
        setWordCount(words.length);
    }, []);

    // Clean up polling and progress on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
            }
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);
    
    // Start progress tracking (assumes 60 seconds for generation)
    const startProgressTracking = useCallback(() => {
        startTimeRef.current = Date.now();
        setGenerationProgress(0);
        
        // Clear any existing progress interval
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }
        
        // Update progress every second
        progressIntervalRef.current = setInterval(() => {
            if (startTimeRef.current) {
                const elapsed = Date.now() - startTimeRef.current;
                const expectedDuration = 60000; // 60 seconds
                const progress = Math.min(90, Math.floor((elapsed / expectedDuration) * 90)); // Cap at 90% until actual completion
                setGenerationProgress(progress);
            }
        }, 1000);
    }, []);
    
    // Stop progress tracking
    const stopProgressTracking = useCallback(() => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
        startTimeRef.current = null;
    }, []);

    // Poll template status using jobId
    const pollTemplateStatus = useCallback(async (jobId: string) => {
        try {
            const response = await makeApiCall(
                API_PATHS.TEMPLATES.STATUS,
                'POST',
                { jobId }
            );

            const status = response as TemplateStatus;
            
            // Store the templateId when it becomes available
            if (status.templateId) {
                setCurrentTemplateId(status.templateId);
            }
            
            if (status.status === 'VALIDATED') {
                setGenerationStatus('success');
                setIsGenerating(false);
                setSuccessMessage('Template successfully updated with your feedback!');
                setGenerationProgress(100); // Set to 100% on success
                
                // Clear polling
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                if (pollTimeoutRef.current) {
                    clearTimeout(pollTimeoutRef.current);
                    pollTimeoutRef.current = null;
                }
                
                // Stop progress tracking
                stopProgressTracking();
                
                // Clear feedback text on success
                setFeedbackText('');
                setWordCount(0);
                
                // Call success callback
                if (onSuccess) {
                    onSuccess(status);
                }
            } else if (status.status === 'FAILED' || status.status === 'GENERATION_FAILED') {
                setGenerationStatus('failed');
                setIsGenerating(false);
                
                // Use errorMessage from API response if available, otherwise provide default message
                const detailedError = status.errorMessage || status.error || 
                    (status.status === 'GENERATION_FAILED' 
                        ? 'Template generation failed. Please review your feedback and try again with more specific instructions.'
                        : 'Template generation failed. Please try again.');
                setError(detailedError);
                setGenerationProgress(0); // Reset progress on failure
                
                // Clear polling
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                if (pollTimeoutRef.current) {
                    clearTimeout(pollTimeoutRef.current);
                    pollTimeoutRef.current = null;
                }
                
                // Stop progress tracking
                stopProgressTracking();
                
                // Don't clear feedback text on failure so user can modify and retry
                // setFeedbackText(''); // Commented out - keep feedback for retry
                
                if (onError) {
                    onError(detailedError);
                }
            }
            // If still GENERATING, SUBMITTED, or PENDING, continue polling
        } catch (err: any) {
            console.error('Error polling template status:', err);
            // Don't stop polling on transient errors
        }
    }, [onSuccess, onError, stopProgressTracking]);

    // Start polling with initial delay
    const startPolling = useCallback((jobId: string) => {
        setGenerationStatus('polling');
        
        // Initial delay of 10 seconds before first poll
        pollTimeoutRef.current = setTimeout(() => {
            // First poll
            pollTemplateStatus(jobId);
            
            // Then poll every 10 seconds
            pollIntervalRef.current = setInterval(() => {
                pollTemplateStatus(jobId);
            }, 10000);
        }, 10000);
    }, [pollTemplateStatus]);

    // Submit feedback
    const submitFeedback = useCallback(async () => {
        if (!feedbackText.trim() || wordCount > 300 || isGenerating) {
            return;
        }

        setIsGenerating(true);
        setGenerationStatus('generating');
        setError(null);
        setSuccessMessage(null);
        setCurrentTemplateId(null);
        
        // Start progress tracking
        startProgressTracking();

        try {
            const response = await makeApiCall(
                API_PATHS.TEMPLATES.GENERATE,
                'POST',
                {
                    jobId: jobId,
                    feedback: feedbackText.trim(),
                    useLegacyHandler: false
                }
            );

            // Use jobId for polling, same as chat interface
            // The templateId will come from GetTemplateStatus response
            if (response.jobId || jobId) {
                startPolling(response.jobId || jobId);
            } else {
                throw new Error('No job ID available for polling');
            }
        } catch (err: any) {
            console.error('Failed to submit feedback:', err);
            setError(err.message || 'Failed to submit feedback. Please try again.');
            setIsGenerating(false);
            setGenerationStatus('failed');
            setGenerationProgress(0);
            stopProgressTracking();
            
            if (onError) {
                onError(err.message || 'Failed to submit feedback');
            }
        }
    }, [feedbackText, wordCount, isGenerating, jobId, startPolling, onError, startProgressTracking, stopProgressTracking]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Clear success message
    const clearSuccessMessage = useCallback(() => {
        setSuccessMessage(null);
    }, []);

    // Reset state
    const reset = useCallback(() => {
        setFeedbackText('');
        setWordCount(0);
        setIsGenerating(false);
        setGenerationStatus('idle');
        setCurrentTemplateId(null);
        setError(null);
        setSuccessMessage(null);
        setGenerationProgress(0);
        
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
        stopProgressTracking();
    }, [stopProgressTracking]);

    return {
        feedbackText,
        setFeedbackText: handleFeedbackChange,
        wordCount,
        isGenerating,
        generationStatus,
        generationProgress,
        currentTemplateId,
        submitFeedback,
        error,
        clearError,
        successMessage,
        clearSuccessMessage,
        reset
    };
};
